import {
  mdiCheckCircleOutline,
  mdiAlertOutline,
  mdiAlertCircleOutline,
} from '@mdi/js'
import { formatLocalDateTime } from '@/utils/datetime'

/**
 * Everything about how an account alert LOOKS and READS, in one place.
 *
 * Two surfaces render these now — the bell dropdown (NotificationItem.vue) and
 * the overview page (views/Notifications.vue) — and a third will eventually.
 * Every one of the decisions below is the kind that goes wrong quietly when it
 * is copied:
 *
 *   • the severity palette — two copies is exactly how one screen ends up with
 *     a red that means "fine" while the other's red means "your campaign is
 *     stopped";
 *   • the paragraph split — if the page honours blank lines and the dropdown
 *     does not, the same notice reads as two different notices;
 *   • the Crisp message — support has to receive the same block whichever
 *     button the customer pressed, or the agent's context depends on where the
 *     customer happened to be standing.
 *
 * Timestamp formatting is NOT duplicated here either: both surfaces import
 * `formatRelativeTime` / `formatLocalDateTime` from `@/utils/datetime`, which
 * already parses bare MySQL datetimes as UTC.
 */

/* ── Severity ────────────────────────────────────────────────────────────── */

/**
 * The word for each severity, for screen readers and for the chat message.
 * `action_required` is deliberately spelled out rather than shown as "Error":
 * nothing has failed, the customer simply has to do something.
 */
export const SEVERITY_WORDS = {
  info: 'Information',
  warning: 'Warning',
  action_required: 'Action required',
}

/*
 * Three severities, three tones, matching ToastItem.vue's palette for the same
 * words so a warning in the bell and a warning in a toast are the same amber.
 *
 * Severity carries a SHAPE as well as a colour (tick / triangle / circle-alert)
 * so it survives greyscale and colour-vision deficiency — the reader has to be
 * able to tell "we topped you up" from "your campaign is stopped" at a glance.
 *
 * Dark mode does not tint the row the way light mode does: there is no `*-950`
 * in Tailwind 2.2's palette and a solid dark red strip would fight the surface,
 * so it leans on the icon tile plus a low-opacity wash.
 *
 * (`bg-opacity-15` is not on Tailwind's default scale. It resolves only because
 * tailwind.config.js extends `opacity` with 15 and 35.)
 */
export const ALERT_TONES = {
  info: {
    icon: mdiCheckCircleOutline,
    tile: 'bg-green-100 text-green-600 dark:bg-green-500 dark:bg-opacity-15 dark:text-green-400',
    unread: 'bg-green-50 dark:bg-green-500 dark:bg-opacity-10',
    label: SEVERITY_WORDS.info,
  },
  warning: {
    icon: mdiAlertOutline,
    tile: 'bg-amber-100 text-amber-600 dark:bg-amber-500 dark:bg-opacity-15 dark:text-amber-400',
    unread: 'bg-amber-50 dark:bg-amber-500 dark:bg-opacity-10',
    label: SEVERITY_WORDS.warning,
  },
  action_required: {
    icon: mdiAlertCircleOutline,
    tile: 'bg-red-100 text-red-600 dark:bg-red-500 dark:bg-opacity-15 dark:text-red-400',
    unread: 'bg-red-50 dark:bg-red-500 dark:bg-opacity-10',
    label: SEVERITY_WORDS.action_required,
  },
}

/** The tone for a severity, defaulting to `info` for anything unrecognised. */
export const toneFor = (severity) => ALERT_TONES[severity] || ALERT_TONES.info

/* ── Body text ───────────────────────────────────────────────────────────── */

/**
 * Split a staff-written body into paragraphs.
 *
 * A blank line in one of these notices is DELIBERATE — staff use it to separate
 * "here is what we changed" from "here is what you need to do", and running the
 * two together is how the second half stops being read. So `\n\n` (or more, and
 * with any stray whitespace on the blank line) starts a new paragraph.
 *
 * A SINGLE newline is kept inside its paragraph rather than being promoted to a
 * paragraph break; the renderer sets `white-space: pre-line`, which turns it
 * into a plain line break. That keeps a short list of bullet-ish lines looking
 * like a list instead of exploding into six paragraphs.
 *
 * Returns an array of strings, always. The caller renders each as a TEXT NODE
 * inside its own <p> — never `v-html`. These strings come from staff input and
 * pass through a database; interpolating them as markup would make a stored XSS
 * out of a support tool.
 *
 * @param {string} body
 * @returns {string[]}
 */
export const toParagraphs = (body) => {
  if (typeof body !== 'string') return []
  return body
    .replace(/\r\n/g, '\n')
    .split(/\n[ \t]*\n+/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
}

/**
 * The whole body as one line, for the clamped preview in the dropdown.
 *
 * Paragraph breaks become spaces on purpose: the preview is three lines inside
 * a `-webkit-box` clamp, and a hard break there wastes one of the three on
 * nothing. The full text with its real paragraphs is one click away.
 */
export const toPreviewText = (body) => toParagraphs(body).join(' ')

/* ── The Crisp hand-off ──────────────────────────────────────────────────── */

/**
 * THE AUTO-PASTE. Composes the message that is pre-typed into support chat when
 * the customer presses "Discuss this".
 *
 * Support opens the conversation already knowing which notice it is about, which
 * job, when it was written and what it said. The customer types only their
 * actual question. Without this they retype a garbled version of a notice
 * support wrote in the first place, and the agent has to go and find it.
 *
 * Kept to the shape of a chat message rather than a dump: a header line, the
 * facts, the body verbatim, then an explicit invitation to add a question — so
 * the caret lands somewhere obvious and the customer does not think the message
 * is already complete.
 *
 * Shared between both surfaces so support receives the identical block whichever
 * screen the customer pressed the button on.
 *
 * @param {Object} alert a normalised alert from useAlerts
 * @returns {string}
 */
export const composeChatMessage = (alert) => {
  const meta = [`Alert #${alert.id}`, SEVERITY_WORDS[alert.severity] || SEVERITY_WORDS.info]

  const when = alert.createdAt ? formatLocalDateTime(alert.createdAt) : ''
  if (when) meta.push(when)

  const lines = [`Re: ${alert.title}`, meta.join(' · ')]
  if (alert.orderLabel) lines.push(`Order: ${alert.orderLabel}`)

  /* The body keeps its own paragraph breaks. Crisp renders a message as
     preformatted text, so the blank lines survive and the block reads the way
     the staff member wrote it. */
  const paragraphs = toParagraphs(alert.body)
  if (paragraphs.length) lines.push('', paragraphs.join('\n\n'))

  lines.push('', '---', 'My question about this:')

  return lines.join('\n')
}

/* ── "Actually seen" ─────────────────────────────────────────────────────── */

/**
 * How long a row has to sit on screen before it counts as read.
 *
 * The dwell is what makes this honest. Marking rows read on arrival would mean
 * one stray click on the bell wipes the unread state off notices nobody
 * scrolled to — precisely the state the badge exists to preserve — and without
 * a dwell, rows that flick past on the way to the bottom would count as read.
 */
export const SEEN_DWELL_MS = 900

/**
 * Mark every alert row currently visible inside `containerEl` as read.
 *
 * Shared by both surfaces because "seen" has to mean the same thing on each.
 * The only difference between them is what counts as the viewport:
 *
 *   • the dropdown scrolls inside its own element, so it passes that element;
 *   • the overview page scrolls the window, so it passes nothing and the
 *     window's own height is used.
 *
 * Rows are found by the `data-alert-id` attribute NotificationItem stamps on
 * itself, so neither caller has to hold refs to individual rows.
 *
 * @param {HTMLElement|null} containerEl element holding the `[data-alert-id]` rows
 * @param {(id: number) => any} markRead  from useAlerts
 * @param {HTMLElement|null} [viewportEl] scrolling ancestor, or null for the window
 */
export const markVisibleAlertsSeen = (containerEl, markRead, viewportEl = null) => {
  if (!containerEl || typeof markRead !== 'function') return

  const bounds = viewportEl
    ? viewportEl.getBoundingClientRect()
    : { top: 0, bottom: window.innerHeight }

  containerEl.querySelectorAll('[data-alert-id]').forEach((row) => {
    const rect = row.getBoundingClientRect()

    /* Any meaningful overlap, not "fully visible": the last row of a full panel
       is usually half-cut and is still plainly being read. The 8px inset stops
       a row that is merely touching the edge from counting. */
    const visible = rect.bottom > bounds.top + 8 && rect.top < bounds.bottom - 8
    if (!visible) return

    const id = Number(row.getAttribute('data-alert-id'))
    if (Number.isFinite(id)) markRead(id)
  })
}
